import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TouchableOpacity, Alert, Linking, Modal, TextInput, Platform, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors, SIZES } from '../constants/theme';
import { whatsappSoporte, SOPORTE_TELEFONO } from '../constants/contacto';
import { useRouter } from 'expo-router';
import ScreenBackground from '../components/ScreenBackground';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import { useGuardiaEscolar } from '../hooks/useGuardiaEscolar';
import { mensajeDeError } from '../utils/mensajeDeError';
import { pesos } from '../utils/dinero';

type Zone = 'A' | 'B' | 'C';

const ZONE_FEES: Record<Zone, number> = {
  A: 3500,
  B: 4500,
  C: 5500,
};

export default function StudentsScreen() {
  const router = useRouter();
  const userFirebaseUid = useAppStore((s) => s.userFirebaseUid);
  const userPhone = useAppStore((s) => s.userPhone);
  const localStudents = useAppStore((s) => s.students);
  const addStudentLocal = useAppStore((s) => s.addStudent);

  // AUDIT FIX SCHOOL — Listener a Firestore para el padre. Antes esta
  // pantalla leía SOLO del store local `globalStudents`, que nunca se
  // sincronizaba con las escrituras que hacía el driver (updateStudentRouteStatus).
  // El padre veía "Esperando ruta" para siempre aunque el chofer marcara
  // "A bordo" o "Entregado". Ahora escuchamos /users/{parentId}/students
  // en tiempo real.
  const [firestoreStudents, setFirestoreStudents] = useState<any[]>([]);
  useEffect(() => {
    if (!userFirebaseUid) return;
    const unsub = firestoreService.listenToParentStudents(userFirebaseUid, (students) => {
      setFirestoreStudents(students);
    });
    return () => { try { unsub?.(); } catch {} };
  }, [userFirebaseUid]);

  // Fuente única de verdad: Firestore si hay sesión; store local si NO
  // (modo demo sin login).
  const globalStudents = userFirebaseUid ? firestoreStudents : localStudents;

  // Modal de alta
  const [modalVisible, setModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<{ name: string; school: string; zone: Zone }>({
    name: '',
    school: '',
    zone: 'A',
  });

  const openAddModal = () => {
    setForm({ name: '', school: '', zone: 'A' });
    setModalVisible(true);
  };

  const handleSaveStudent = async () => {
    const cleanName = form.name.trim();
    const cleanSchool = form.school.trim();
    if (!cleanName || !cleanSchool) {
      Alert.alert('Datos incompletos', 'Ingresa el nombre del estudiante y el colegio.');
      return;
    }

    const studentData = {
      name: cleanName,
      school: cleanSchool,
      zone: form.zone,
      fee: ZONE_FEES[form.zone],
      driver: 'Pendiente',
      status: 'Esperando ruta',
    };

    if (!userFirebaseUid) {
      // Sin sesión: solo guarda local. Útil en modo demo de la app.
      addStudentLocal(studentData as any);
      setModalVisible(false);
      return;
    }

    setSubmitting(true);
    try {
      // 1) Persistir en /users/{uid}/students (sub-colección del padre) Y
      // en /students root con schoolId — esta segunda escritura es la que
      // permite al director del colegio VER a este estudiante. Antes solo
      // escribíamos la sub-colección y el director tenía "0 Estudiantes".
      await firestoreService.addStudent(userFirebaseUid, {
        ...studentData,
        parentId: userFirebaseUid,
        parentPhone: userPhone || null,
      });

      // El listener `listenToParentStudents` propagará el nuevo estudiante
      // a la UI automáticamente — no duplicamos en el store local para
      // evitar cards fantasma cuando falla la escritura.

      setModalVisible(false);
      Alert.alert('Estudiante registrado', '¿Deseas buscarle ruta escolar ahora?', [
        { text: 'Más tarde', style: 'cancel' },
        { text: 'Buscar ruta', onPress: () => router.push('/school-driver-directory') },
      ]);
    } catch (e: any) {
      console.error('registerStudent error', e);
      Alert.alert(
        'Error',
        mensajeDeError(e, 'No se pudo guardar el estudiante. Verifica tu conexión e inténtalo de nuevo.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  // EMERGENCIA ESCOLAR.
  // Antes ofrecía "Llamar al Chofer" y marcaba 809-555-0000 — un número que NO
  // EXISTE. Un padre tocando esto por su hijo y escuchando "número no
  // disponible" es el peor momento imaginable para esta app. Además el botón
  // no tenía de dónde sacar el teléfono: los estudiantes nunca guardan el del
  // chofer. Ahora solo se ofrecen vías que de verdad responden, y el teléfono
  // del chofer aparece únicamente si el estudiante lo tiene registrado.
  const handleSOS = () => {
    const conChofer = [...firestoreStudents, ...localStudents]
      .find((s: any) => s?.driverPhone);

    const opciones: any[] = [{ text: 'Cancelar', style: 'cancel' }];

    if (conChofer?.driverPhone) {
      opciones.push({
        text: `Llamar al chofer${conChofer.driver ? ` (${conChofer.driver})` : ''}`,
        onPress: () => {
          Linking.openURL(`tel:${conChofer.driverPhone}`).catch(() =>
            Alert.alert('No se pudo llamar', `Marca directamente: ${conChofer.driverPhone}`)
          );
        },
      });
    }

    opciones.push({
      text: 'Escribir a Go Safely',
      onPress: () => {
        Linking.openURL(
          whatsappSoporte('EMERGENCIA ESCOLAR: necesito ayuda urgente con el transporte de mi hijo/a.')
        ).catch(() => Alert.alert('Escríbenos', `WhatsApp: ${SOPORTE_TELEFONO}`));
      },
    });

    opciones.push({
      text: 'Llamar al 911',
      style: 'destructive',
      onPress: () => {
        Linking.openURL('tel:911').catch(() =>
          Alert.alert('No se pudo llamar', 'Marca el 911 directamente desde tu teléfono.')
        );
      },
    });

    Alert.alert(
      '🚨 Emergencia escolar',
      conChofer?.driverPhone
        ? '¿Con quién quieres comunicarte?'
        : 'Todavía no tienes un chofer asignado con teléfono registrado. Puedes escribirnos o llamar al 911.',
      opciones
    );
  };

  // Vertical escolar oculto: ver VERTICAL_ESCOLAR_VISIBLE en featureFlags.ts
  const escolarOculto = useGuardiaEscolar();
  if (escolarOculto) return null;

  return (
    <ScreenBackground>
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.header}>
          <TouchableOpacity
            onPress={() => (router.canGoBack() ? router.back() : router.replace('/'))}
            style={styles.backButton}
          >
            <Ionicons name="arrow-back" size={24} color={Colors.text} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Transporte de mis Hijos</Text>
          <TouchableOpacity onPress={openAddModal}>
            <Ionicons name="person-add" size={24} color={Colors.primary} />
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={styles.container}>
          <Text style={styles.subtitle}>
            Supervisa el transporte escolar de tus hijos en tiempo real con choferes certificados.
          </Text>

          {/* CONSENTIMIENTO PARENTAL, DICHO EN VOZ ALTA.
              Go Safely NO es una app para niños: el menor no tiene cuenta, no
              inicia sesión y no usa la app. La cuenta es del padre, madre o
              tutor, y es él quien decide inscribir a su hijo y ver el traslado.
              Eso hay que declararlo en la consola de Google Play (Público
              objetivo = solo adultos) y también decirlo aquí, donde el revisor
              lo ve: si el texto no lo aclara, la pantalla parece dirigida a
              menores aunque no lo esté. */}
          <View style={styles.avisoTutor}>
            <Ionicons name="shield-checkmark-outline" size={20} color={Colors.primary} style={{ marginTop: 1 }} />
            <Text style={styles.avisoTutorTexto}>
              <Text style={{ fontWeight: 'bold' }}>Esta cuenta es tuya, no de tu hijo.</Text>{' '}
              Go Safely no es una aplicación para menores: tu hijo no tiene cuenta, no inicia sesión
              ni usa la app. Eres tú, como padre, madre o tutor, quien lo inscribe y decide quién
              puede ver su traslado. Solo tú y la dirección del colegio que elijas ven sus datos, y
              puedes eliminarlos cuando quieras.
            </Text>
          </View>

          {globalStudents.length === 0 ? (
            <View style={{ alignItems: 'center', paddingVertical: 40 }}>
              <Ionicons name="school-outline" size={48} color={Colors.textSecondary} />
              <Text style={{ color: Colors.textSecondary, marginTop: 16, textAlign: 'center' }}>
                No tienes hijos inscritos en rutas escolares aún.
              </Text>
              <TouchableOpacity
                style={{ marginTop: 20, backgroundColor: Colors.primary, paddingHorizontal: 20, paddingVertical: 12, borderRadius: 12 }}
                onPress={openAddModal}
              >
                <Text style={{ color: '#FFF', fontWeight: 'bold' }}>Registrar a mi Hijo</Text>
              </TouchableOpacity>
            </View>
          ) : (
            globalStudents.map((student: any) => (
              <View key={student.id} style={styles.studentCard}>
                <View style={styles.cardHeader}>
                  <View style={styles.studentInfoRow}>
                    <View style={[styles.avatar, { backgroundColor: '#F0F0F0', justifyContent: 'center', alignItems: 'center' }]}>
                      <Ionicons name="person" size={24} color="#999" />
                    </View>
                    <View>
                      <Text style={styles.studentName}>{student.name}</Text>
                      <Text style={styles.schoolName}>{student.school}</Text>
                    </View>
                  </View>
                  {student.zone ? (
                    <View style={styles.zoneBadge}>
                      <Text style={styles.zoneText}>Zona {student.zone}</Text>
                    </View>
                  ) : null}
                </View>

                <View style={styles.divider} />

                <View style={styles.routeInfo}>
                  <View style={styles.infoCol}>
                    <Text style={styles.infoLabel}>Chofer Asignado</Text>
                    <Text style={styles.infoValue}>{student.driver ?? 'Pendiente'}</Text>
                  </View>
                  <View style={styles.infoCol}>
                    <Text style={styles.infoLabel}>Estado</Text>
                    <Text
                      style={[
                        styles.infoValue,
                        { color: String(student.status ?? '').includes('ruta') ? '#4CAF50' : '#FF9800' },
                      ]}
                    >
                      {student.status ?? 'Sin asignar'}
                    </Text>
                  </View>
                </View>

                <View style={styles.footerRow}>
                  <View>
                    <Text style={styles.feeLabel}>Mensualidad</Text>
                    <Text style={styles.feeValue}>
                      {pesos(typeof student.fee === 'number' ? student.fee : 0)}
                    </Text>
                  </View>

                  <View style={styles.actionButtons}>
                    <TouchableOpacity style={styles.trackBtn} onPress={() => router.push('/(tabs)/map')}>
                      <Ionicons name="location" size={16} color="#FFF" />
                      <Text style={styles.trackBtnText}>Rastrear</Text>
                    </TouchableOpacity>

                    <TouchableOpacity style={styles.sosBtn} onPress={handleSOS}>
                      <Ionicons name="warning" size={16} color="#FFF" />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            ))
          )}

          <View style={styles.infoBox}>
            <Ionicons name="information-circle" size={24} color={Colors.primary} />
            <Text style={styles.infoBoxText}>
              Las tarifas se basan en radios de distancia: Zona A (0-5km) = RD$ 3,500, Zona B (5-10km) = RD$ 4,500, Zona C (+10km) = RD$ 5,500.
            </Text>
          </View>

          <View style={{ height: 40 }} />
        </ScrollView>

        {/* MODAL DE ALTA.
            onRequestClose (H37, auditoría 16-sep-2026): sin él, en Android el
            botón Atrás se lo tragaba el Modal y no pasaba nada. Hace lo mismo
            que la X del encabezado. */}
        <Modal visible={modalVisible} animationType="slide" transparent onRequestClose={() => setModalVisible(false)}>
          <View style={styles.modalOverlay}>
            <View style={styles.modalCard}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Registrar Estudiante</Text>
                <TouchableOpacity onPress={() => setModalVisible(false)}>
                  <Ionicons name="close" size={26} color={Colors.text} />
                </TouchableOpacity>
              </View>

              <Text style={styles.modalLabel}>Nombre completo del estudiante</Text>
              <TextInput
                style={styles.modalInput}
                placeholder="Ej. María Pérez"
                placeholderTextColor={Colors.textSecondary}
                value={form.name}
                onChangeText={(t) => setForm((f) => ({ ...f, name: t }))}
                editable={!submitting}
              />

              <Text style={styles.modalLabel}>Colegio</Text>
              <TextInput
                style={styles.modalInput}
                placeholder="Ej. Colegio Loyola"
                placeholderTextColor={Colors.textSecondary}
                value={form.school}
                onChangeText={(t) => setForm((f) => ({ ...f, school: t }))}
                editable={!submitting}
              />

              <Text style={styles.modalLabel}>Zona (según distancia al colegio)</Text>
              <View style={styles.zoneRow}>
                {(['A', 'B', 'C'] as Zone[]).map((z) => (
                  <TouchableOpacity
                    key={z}
                    style={[
                      styles.zoneOption,
                      form.zone === z && styles.zoneOptionActive,
                    ]}
                    onPress={() => setForm((f) => ({ ...f, zone: z }))}
                    disabled={submitting}
                  >
                    <Text
                      style={[
                        styles.zoneOptionText,
                        form.zone === z && styles.zoneOptionTextActive,
                      ]}
                    >
                      Zona {z}
                    </Text>
                    <Text
                      style={[
                        styles.zoneOptionFee,
                        form.zone === z && styles.zoneOptionTextActive,
                      ]}
                    >
                      {pesos(ZONE_FEES[z])}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              <TouchableOpacity
                style={[styles.modalSaveBtn, submitting && { opacity: 0.7 }]}
                onPress={handleSaveStudent}
                disabled={submitting}
              >
                {submitting ? (
                  <ActivityIndicator color="#FFF" />
                ) : (
                  <Text style={styles.modalSaveBtnText}>Guardar Estudiante</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </Modal>
      </SafeAreaView>
    </ScreenBackground>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: '#FFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E0E1E6',
  },
  backButton: {
    padding: 8,
    marginLeft: -8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: Colors.text,
  },
  container: {
    padding: 20,
  },
  subtitle: {
    fontSize: 14,
    color: Colors.textSecondary,
    marginBottom: 20,
    lineHeight: 20,
  },
  avisoTutor: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#EEF4FF',
    borderWidth: 1,
    borderColor: '#C7D9FF',
    borderRadius: 14,
    padding: 14,
    marginBottom: 20,
  },
  avisoTutorTexto: {
    flex: 1,
    marginLeft: 10,
    fontSize: 12.5,
    lineHeight: 18,
    color: Colors.text,
  },
  studentCard: {
    backgroundColor: '#FFF',
    borderRadius: 20,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.05,
    shadowRadius: 15,
    elevation: 3,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  studentInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginRight: 15,
  },
  studentName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: Colors.text,
  },
  schoolName: {
    fontSize: 14,
    color: Colors.textSecondary,
    marginTop: 2,
  },
  zoneBadge: {
    backgroundColor: 'rgba(0, 45, 98, 0.1)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  zoneText: {
    color: Colors.primary,
    fontWeight: 'bold',
    fontSize: 12,
  },
  divider: {
    height: 1,
    backgroundColor: '#F0F0F0',
    marginVertical: 15,
  },
  routeInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  infoCol: {
    flex: 1,
  },
  infoLabel: {
    fontSize: 12,
    color: Colors.textSecondary,
    marginBottom: 4,
  },
  infoValue: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text,
  },
  footerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#F9FAFC',
    padding: 15,
    borderRadius: 16,
    marginTop: 5,
  },
  feeLabel: {
    fontSize: 12,
    color: Colors.textSecondary,
  },
  feeValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: Colors.primary,
  },
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  trackBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.accent,
    paddingHorizontal: 15,
    paddingVertical: 10,
    borderRadius: 12,
    marginRight: 10,
  },
  trackBtnText: {
    color: '#FFF',
    fontWeight: 'bold',
    fontSize: 13,
    marginLeft: 5,
  },
  sosBtn: {
    backgroundColor: '#FF3B30',
    width: 38,
    height: 38,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  infoBox: {
    flexDirection: 'row',
    backgroundColor: 'rgba(0, 45, 98, 0.05)',
    padding: 15,
    borderRadius: 12,
    alignItems: 'flex-start',
  },
  infoBoxText: {
    flex: 1,
    fontSize: 13,
    color: Colors.primary,
    marginLeft: 10,
    lineHeight: 18,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  modalCard: {
    backgroundColor: '#FFF',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 24,
    paddingBottom: Platform.OS === 'ios' ? 40 : 24,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 18,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Colors.text,
  },
  modalLabel: {
    fontSize: 13,
    color: Colors.textSecondary,
    fontWeight: '600',
    marginBottom: 6,
    marginTop: 10,
  },
  modalInput: {
    borderWidth: 1,
    borderColor: '#E4E7EB',
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    color: Colors.text,
    backgroundColor: '#F5F7FA',
  },
  zoneRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
  },
  zoneOption: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#E4E7EB',
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: 'center',
    backgroundColor: '#F5F7FA',
  },
  zoneOptionActive: {
    backgroundColor: Colors.primary,
    borderColor: Colors.primary,
  },
  zoneOptionText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: Colors.text,
  },
  zoneOptionTextActive: {
    color: '#FFF',
  },
  zoneOptionFee: {
    fontSize: 11,
    color: Colors.textSecondary,
    marginTop: 2,
  },
  modalSaveBtn: {
    backgroundColor: Colors.primary,
    marginTop: 20,
    paddingVertical: 16,
    borderRadius: 14,
    alignItems: 'center',
  },
  modalSaveBtnText: {
    color: '#FFF',
    fontWeight: 'bold',
    fontSize: 16,
  },
});
